import { Col, Container, Row } from "reactstrap";
import s from "../organizationSetup/styles.scss";
import WizardBreadcrumb from "../common/WizardBreadcrumb";
import React, { useState, useEffect } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import { customUrlInputSchema } from "./customUrlInput";
import { stripeEmailInputSchema } from "./stripeIntegration";
import CustomUrlInput from "./customUrlInput";
import StripeIntegrationStep from "./stripeIntegration";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { serviceCaller, ServiceCallerProps } from "../../../../composition";
import LoadingSpinner from "../../../../../toolkit/loadingSpinner";
import SnackbarNotification from "../../../../../toolkit/snackbarNotification";
import { usePaymentSetupData } from "./hooks/usePaymentSetupData";
import { useSnackbar } from "../../../../../hooks/useSnackbar";

const paymentSetupWizardSchema = yup.object().shape({
  paymentMethod: yup.string(),
  ...customUrlInputSchema.fields,
  ...stripeEmailInputSchema.fields,
});

type ViewType = "giveSpark" | "customUrl";

interface PaymentSetupProps extends ServiceCallerProps {
  embedded?: boolean;
  onExit?: () => void;
}

const PaymentSetup: React.FC<PaymentSetupProps> = ({
  embedded = true,
  onExit,
  currentCharity,
  config,
  serviceAccessToken,
}) => {
  const { snackbarProps, showSnackbar } = useSnackbar();
  const [currentView, setCurrentView] = useState<ViewType>("giveSpark");

  const methods = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(paymentSetupWizardSchema),
    defaultValues: {
      paymentMethod: "giveSpark",
      customDonationUrl: "",
      stripeInstructionEmail: "",
    },
  });

  const { loading, error, stripeConfig, paymentSetupData } = usePaymentSetupData({
    charityId: currentCharity?.id,
    methods,
  });

  useEffect(() => {
    if (!loading && paymentSetupData?.paymentMethod) {
      setCurrentView(paymentSetupData.paymentMethod === "customUrl" ? "customUrl" : "giveSpark");
    }
  }, [loading, paymentSetupData?.paymentMethod]);

  const handleUseCustomUrl = () => {
    methods.setValue("paymentMethod", "customUrl");
    setCurrentView("customUrl");
  };

  const handleBackToGiveSpark = () => {
    methods.setValue("paymentMethod", "giveSpark");
    setCurrentView("giveSpark");
  };

  const renderMainContent = () => {
    if (error) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <div className="text-center">
            <h5 className="text-danger">Unable to load payment setup</h5>
            <p className="text-muted">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <FormProvider {...methods}>
        <form noValidate className={s.fadeIn}>
          {currentView === "giveSpark" ? (
            <StripeIntegrationStep
              onExit={onExit}
              stripeConfig={stripeConfig}
              charityId={currentCharity?.id}
              config={config}
              serviceAccessToken={serviceAccessToken}
              onUseCustomUrl={handleUseCustomUrl}
              showSnackbar={showSnackbar}
            />
          ) : (
            <CustomUrlInput
              onExit={onExit}
              showSnackbar={showSnackbar}
              onBack={handleBackToGiveSpark}
            />
          )}
        </form>
      </FormProvider>
    );
  };

  return (
    <>
      <Container fluid className={embedded ? "vh-100" : s.fullScreenContainer}>
        <Row className="h-100">
          <Col md="12" className={s.wizardContainer}>
            {!embedded && loading && (
              <WizardBreadcrumb onBack={onExit} showBreadcrumb />
            )}
            {loading ? (
              <div className="mt-5">
                <LoadingSpinner
                  title="Loading Payment Setup"
                  message="Please wait while we load your data and initialize the wizard."
                  className={s.fadeIn}
                />
              </div>
            ) : (
              <div className={embedded ? undefined : s.wizardShell}>
                {!embedded && (
                  <WizardBreadcrumb onBack={onExit} showBreadcrumb />
                )}
                <div className={embedded ? undefined : s.wizardStepPanelStandalone}>
                  {renderMainContent()}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

export default serviceCaller(withStyles(s)(PaymentSetup));
